import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { PuzzleGameModel } from 'projects/tools/src/lib/store/puzzle-game.model';
import { PuzzleFrameworkService } from 'projects/tools/src/lib/puzzle-framework-module/puzzle-framework.service';
import { PuzzleGameState } from 'projects/tools/src/lib/store/puzzle-game.state';
import { Button } from 'projects/tools/src/lib/shared-module/buttons/button.def';
import { PuzzleAnalyticsDialogComponent } from 'projects/tools/src/lib/shared-module/puzzle-analytics/puzzle-analytics-dialog.component';
import { RevokeConsent, SetConsent } from 'projects/tools/src/lib/store/puzzle-analytics-consent.actions';
import { PuzzleTimeRecordModel } from '../../store/puzzle-time-record.model';
import { PuzzleTimeRecordState } from '../../store/puzzle-time-record.state';
import { AlertController, ModalController } from '@ionic/angular/standalone';
import { LeaderboardService } from 'projects/tools/src/lib/shared-module/leaderboard/leaderboard.service';
import { PuzzleStorageEngine } from '../../../../../web/src/app/puzzle-storage-engine.service';

// declare function gtag(a: any, b: any, c: any, d?: any, e?: any, f?: any): any;

@Injectable({
  providedIn: 'root',
})
export class PuzzleAnalyticsService {
  private googleAnalyticsCode: string;
  private scriptsAdded = false;
  private consent = false;
  public enabled = false;
  private puzzleID: string;
  private puzzleGameSubscription: Subscription;
  private timeRecordSubscription: Subscription;
  private puzzleGame: PuzzleGameModel;
  private progress = 0;
  private totalJokers = 1;
  private finished = false;
  private gameTime;

  constructor(
    // private config: Config,
    private store: Store,
    private puzzleEvents: PuzzleFrameworkService,
    private translate: TranslateService,
    private framework: PuzzleFrameworkService,
    private ionicAlert: AlertController,
    private ionicModal: ModalController,
    private leaderboard: LeaderboardService,
    private storageEngine: PuzzleStorageEngine
  ) {
    this.totalJokers = this.framework.puzzleFrameworkConfig.jokerButton.value;

    this.store
      .select((state) => state.puzzleID)
      .subscribe((puzzleID) => {
        this.puzzleID = puzzleID;

        if (this.timeRecordSubscription) {
          this.timeRecordSubscription.unsubscribe();
        }

        this.timeRecordSubscription = this.store
          .select(PuzzleTimeRecordState.getTimeRecordByID)
          .pipe(map((filterFn) => filterFn(puzzleID)))
          .subscribe((timeRecord: PuzzleTimeRecordModel) => {
            if (!timeRecord) {
              return;
            }
            this.gameTime = timeRecord.timeRecord.gameTime;
          });

        if (this.puzzleGameSubscription) {
          this.puzzleGameSubscription.unsubscribe();
        }

        this.puzzleGameSubscription = this.store
          .select(PuzzleGameState.getByID(puzzleID))
          .subscribe((puzzleGame: PuzzleGameModel) => {
            if (!puzzleGame) {
              return;
            }

            const finished = this.puzzleGame && this.puzzleGame.finished !== puzzleGame.finished && puzzleGame.finished;

            this.puzzleGame = puzzleGame;

            if (finished) {
              this.sendEvent('finished', 'progress', 'Time', this.gameTime, true);
              this.sendEvent('finished', 'progress', 'Moves', this.puzzleGame.moveCounter, true);
              this.sendEvent('finished', 'progress', 'Jokers used (%)', this.getJokerPercent(), true);
            }

            if (this.consent) {
              this.initializeAnalytics();
            }
          });
      });

    this.puzzleEvents.buttonPressed.subscribe((event) => {
      if (event === Button.Privacy) {
        this.openPrivacyDialog();
      }
    });

    this.store
      .select((state) => state.cookieConsent)
      .subscribe((consent) => {
        // Potentially restoring old consent.
        // The first time we check for the consent, so if old consent is detected, we also store it as new consent.
        // if (!consent && this.storageEngine.oldConsentGiven()) {
        if (this.storageEngine.oldConsentGiven()) {
          consent = true; // Old consent is given
          this.store.dispatch(new SetConsent());
        }
        if (this.consent !== consent) {
          // Consent is revoked
          this.consent = consent;
          if (this.consent === false) {
            window.location.reload();
          }

          if (this.consent === true) {
            this.initializeAnalytics();
          }
        }
        // When consent is false, always delete cookies
        if (this.consent === false) {
          this.deleteCookies();
        }
      });

    this.framework.puzzleConfigLoaded$.subscribe(() => {
      this.googleAnalyticsCode = this.framework.puzzleFrameworkConfig.googleAnalyticsCode;
      if (this.googleAnalyticsCode) {
        this.enabled = true;
      }
    });

    window.setInterval(() => {
      if (this.consent && this.puzzleGame && this.puzzleGame.started) {
        if (!this.puzzleGame.finished) {
          this.sendEvent('update', 'progress', 'Progress (%)', this.progress, true);
          this.sendEvent('update', 'progress', 'Time', this.gameTime, true);
          this.sendEvent('update', 'progress', 'Moves', this.puzzleGame.moveCounter, true);
          this.sendEvent('update', 'progress', 'Jokers used (%)', this.getJokerPercent(), true);
        }
      }
    }, this.framework.puzzleFrameworkConfig.googleAnalyticsUpdateInterval * 60000);
  }

  async openPrivacyDialog(requestConsent = true) {
    const modal = await this.ionicModal.create({
      component: PuzzleAnalyticsDialogComponent,
      cssClass: 'puzzle-modal',
      componentProps: { requestConsent },
      backdropDismiss: false,
    });

    await modal.present();

    const result = await modal.onDidDismiss<boolean>();

    if (!result.data) {
      return;
    }

    const reloadAccepted = await this.confirmCancelOrReload();

    if (!reloadAccepted) {
      await this.openPrivacyDialog();
    }
  }

  private getJokerPercent() {
    return this.totalJokers !== 0 ? 100 : (1 - this.puzzleGame.jokerCount / this.totalJokers) * 100;
  }

  public async confirmCancelOrReload() {
    const resolved = await Promise.all([
      this.translate.get('ANALYTICS.restart_dialog.title').toPromise(),
      this.translate.get('ANALYTICS.restart_dialog.message').toPromise(),
      this.translate.get('ANALYTICS.restart_dialog.warning').toPromise(),
      this.translate.get('PUZZLE.dialog.button.no').toPromise(),
      this.translate.get('PUZZLE.dialog.button.yes').toPromise(),
    ]);

    // return new Promise<boolean>(resolve => {
    const alert = await this.ionicAlert.create({
      header: resolved[0],
      message: resolved[2],
      subHeader: resolved[1],
      backdropDismiss: false,
      cssClass: 'puzzle-alert',
      buttons: [
        {
          text: resolved[3],
          role: 'cancel',
          cssClass: 'primary',
        },
        {
          text: resolved[4],
          role: 'confirm',
          cssClass: 'primary',
        },
      ],
    });

    if (this.leaderboard.canPauseSilently()) {
      this.framework.pauseCurrentGame();
    }
    await alert.present();

    const result = await alert.onDidDismiss();

    this.framework.resumeCurrentGame();

    if (result.role === 'confirm') {
      this.store.dispatch(new RevokeConsent());
      return true;
    }

    return false;
  }

  /**
   * Adds analytics scripts to the DOM
   * exits early if script is already added
   * TODO: Maybe implement script loader and combine with ImaAdService.ensureIMASCripts().
   * Inspired by https://medium.com/better-programming/angular-load-external-javascript-file-dynamically-3d14dde815cb
   */
  private ensureAnalyticsScripts() {
    if (this.scriptsAdded) {
      return;
    }

    const head = document.getElementsByTagName('head')[0];
    const googleAnalyticsFirstScript = document.createElement('script');
    googleAnalyticsFirstScript.async = true;
    googleAnalyticsFirstScript.src = 'https://www.googletagmanager.com/gtag/js?id=' + this.googleAnalyticsCode;

    const googleAnalyticsSecondScript = document.createElement('script');
    googleAnalyticsSecondScript.innerHTML =
      'window.dataLayer = window.dataLayer || [];\n' +
      'function gtag(){dataLayer.push(arguments);}\n' +
      'gtag("js", new Date());\n' +
      'gtag("config", "' +
      this.googleAnalyticsCode +
      '", { "anonymize_ip": true });\n';
    head.insertBefore(googleAnalyticsSecondScript, head.firstChild);
    head.insertBefore(googleAnalyticsFirstScript, head.firstChild);

    this.scriptsAdded = true;
  }

  /**
   * Initializes Analytics
   * Exits early if no analytics code is set, analytics is already initialized or no consent is given
   */
  public async initializeAnalytics(sendPageView = true) {
    if (!this.googleAnalyticsCode || this.scriptsAdded || !this.consent || !this.puzzleGame) {
      return false;
    }
    this.ensureAnalyticsScripts();

    if (sendPageView) {
      const puzzleID = await this.store.selectOnce((state) => state.puzzleID).toPromise();
      this.sendPageView({ puzzleID, anonymize_ip: true, gameType: this.puzzleGame.gameType });
    }

    return true;
  }

  private deleteCookies() {
    // Trying to delete all cookies. From https://stackoverflow.com/a/44164390/4396362
    // document.cookie.split(';').forEach(function(c) {
    //   document.cookie = c.trim().split('=')[0] + '=;' + 'expires=Thu, 01 Jan 1970 00:00:00 UTC;';
    // });
    // delete including cookies with paths. From https://stackoverflow.com/a/33366171/4396362
    const cookies = document.cookie.split('; ');
    for (let c = 0; c < cookies.length; c++) {
      const d = window.location.hostname.split('.');
      while (d.length > 0) {
        const cookieBase =
          encodeURIComponent(cookies[c].split(';')[0].split('=')[0]) +
          '=; expires=Thu, 01-Jan-1970 00:00:01 GMT; domain=' +
          d.join('.') +
          ' ;path=';
        const p = location.pathname.split('/');
        document.cookie = cookieBase + '/';
        while (p.length > 0) {
          document.cookie = cookieBase + p.join('/');
          p.pop();
        }
        d.shift();
      }
    }
    // document.cookie = '_ga=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    // document.cookie = '_gid=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    // document.cookie = '_gat_gtag_UA_152013669_1=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  }

  private sendPageView(data = {}) {
    if (this.scriptsAdded) {
      // @ts-ignore
      gtag('config', this.googleAnalyticsCode, data);
    }
  }

  public sendEvent(event: string, category: string, label: string, val: number, interaction = true) {
    this.initializeAnalytics();

    if (this.scriptsAdded) {
      // @ts-ignore
      gtag('event', event, {
        event_category: category,
        event_label: label,
        value: val,
        non_interaction: !interaction,
        anonymize_ip: true,
      });
    }
  }

  public setProgress(value) {
    this.progress = Math.round(value);
  }
}
